import { useCallback, useEffect, useRef, useState } from "react";
import { acquireBandLeaderChannel, releaseBandLeaderChannel } from "../realtime/bandLeaderChannel";
import { getNextBoundaryBeat } from "../utils/timing";

const getMsPerBeat = (bpm) => 60000 / bpm;

export default function useFollowerTransportSync({
  bandId,
  selectedLeaderId,
  timelineSections,
  measureBoundaryBeats,
  getSectionIndex,
  onSectionIndex,
  currentSongId,
  currentArrangementId,
  smoothScroll = false,
  leadInMeasures = 0,
  onLeaderSongChange,
  onStopFollow,
}) {
  const [isOnline, setIsOnline] = useState(() => navigator?.onLine ?? true);
  const channelRef = useRef(null);
  const channelBandIdRef = useRef(null);
  const rafRef = useRef(null);
  const lastSectionIdRef = useRef(null);
  const pendingJumpRef = useRef(null);
  const lastSongChangeByLeaderRef = useRef(new Map());
  const lastLeaderIdRef = useRef(null);
  const lastSyncRequestRef = useRef(null);
  const syncRef = useRef({
    bpm: null,
    beatsPerMeasure: null,
    anchorUtc: null,
    delayBeats: 0,
  });

  const stopSync = useCallback(
    (reason) => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      lastSectionIdRef.current = null;
      pendingJumpRef.current = null;
      syncRef.current = {
        bpm: null,
        beatsPerMeasure: null,
        anchorUtc: null,
        delayBeats: 0,
      };
      if (onStopFollow) onStopFollow(reason);
    },
    [onStopFollow],
  );

  useEffect(() => {
    const onOnline = () => setIsOnline(true);
    const onOffline = () => setIsOnline(false);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, []);

  useEffect(() => {
    if (!bandId || !isOnline || !selectedLeaderId) {
      if (channelRef.current) {
        releaseBandLeaderChannel(channelBandIdRef.current);
        channelRef.current = null;
        channelBandIdRef.current = null;
      }
      stopSync(selectedLeaderId ? "offline-or-missing" : "no-leader");
      return;
    }
    const channel = acquireBandLeaderChannel({ bandId });

    const unsubscribeSongChange = channel.addBroadcastListener("leader_song_change", ({ payload }) => {
      if (!payload || !payload.leader_user_id) return;
      lastSongChangeByLeaderRef.current.set(String(payload.leader_user_id), payload);
      if (!selectedLeaderId || String(payload.leader_user_id) !== String(selectedLeaderId)) return;
      const leaderSongId = payload.song_id;
      if (!leaderSongId) return;
      if (!currentSongId || String(leaderSongId) !== String(currentSongId)) {
        onLeaderSongChange?.(payload);
        return;
      }
      if (payload.arrangement_id && currentArrangementId && String(payload.arrangement_id) !== String(currentArrangementId)) {
        onLeaderSongChange?.(payload);
      }
    });

    const unsubscribePlay = channel.addBroadcastListener("leader_play", ({ payload }) => {
      if (!selectedLeaderId || !payload || String(payload.leader_user_id) !== String(selectedLeaderId)) return;
      const bpm = Number(payload.bpm);
      const beatsPerMeasure = Number(payload.beats_per_measure || payload.beatsPerMeasure);
      const anchorUtc = Number(payload.timeline_anchor_utc);
      if (!Number.isFinite(bpm) || bpm <= 0) return;
      if (!Number.isFinite(beatsPerMeasure) || beatsPerMeasure <= 0) return;
      if (!Number.isFinite(anchorUtc)) return;
      const leadInBeats = (Number(leadInMeasures) || 0) * beatsPerMeasure;
      const msPerBeat = getMsPerBeat(bpm);
      const delayBeats = Math.max(0, leadInBeats);
      const adjustedAnchorUtc = anchorUtc + Math.min(0, leadInBeats) * msPerBeat;
      syncRef.current = {
        bpm,
        beatsPerMeasure,
        anchorUtc: adjustedAnchorUtc,
        delayBeats,
      };
      lastSectionIdRef.current = null;
      pendingJumpRef.current = null;
    });

    const unsubscribeJump = channel.addBroadcastListener("leader_jump", ({ payload }) => {
      if (!selectedLeaderId || !payload || String(payload.leader_user_id) !== String(selectedLeaderId)) return;
      const targetSectionIndex = Number(payload.targetSectionIndex);
      if (!Number.isFinite(targetSectionIndex)) return;
      pendingJumpRef.current = { targetSectionIndex };
    });

    const unsubscribeStop = channel.addBroadcastListener("leader_stop", ({ payload }) => {
      if (!selectedLeaderId || !payload || String(payload.leader_user_id) !== String(selectedLeaderId)) return;
      lastSectionIdRef.current = null;
      pendingJumpRef.current = null;
      syncRef.current = {
        bpm: null,
        beatsPerMeasure: null,
        anchorUtc: null,
        delayBeats: 0,
      };
    });

    channelRef.current = channel;
    channelBandIdRef.current = bandId;
    return () => {
      unsubscribeSongChange();
      unsubscribePlay();
      unsubscribeJump();
      unsubscribeStop();
      releaseBandLeaderChannel(channelBandIdRef.current);
      channelRef.current = null;
      channelBandIdRef.current = null;
    };
  }, [bandId, currentArrangementId, currentSongId, isOnline, leadInMeasures, onLeaderSongChange, selectedLeaderId, stopSync]);

  useEffect(() => {
    if (!selectedLeaderId) {
      lastSyncRequestRef.current = null;
      lastLeaderIdRef.current = null;
      stopSync("no-leader");
      return;
    }
    if (!isOnline) return;
    if (String(lastLeaderIdRef.current || "") !== String(selectedLeaderId)) {
      lastLeaderIdRef.current = String(selectedLeaderId);
      stopSync("leader-changed");
    }
    const payload = lastSongChangeByLeaderRef.current.get(String(selectedLeaderId));
    if (!payload) return;
    const leaderSongId = payload.song_id;
    if (!leaderSongId) return;
    if (!currentSongId || String(leaderSongId) !== String(currentSongId)) {
      onLeaderSongChange?.(payload);
      return;
    }
    if (payload.arrangement_id && currentArrangementId && String(payload.arrangement_id) !== String(currentArrangementId)) {
      onLeaderSongChange?.(payload);
    }
  }, [currentArrangementId, currentSongId, isOnline, onLeaderSongChange, selectedLeaderId, stopSync]);

  useEffect(() => {
    if (!selectedLeaderId || !isOnline || !channelRef.current) return;
    const requestKey = `${bandId || ""}:${selectedLeaderId}`;
    if (lastSyncRequestRef.current === requestKey) return;
    lastSyncRequestRef.current = requestKey;
    channelRef.current.send("leader_sync_request", {
      leader_user_id: selectedLeaderId,
      requested_at: Date.now(),
    });
  }, [bandId, isOnline, selectedLeaderId]);

  useEffect(() => {
    if (!selectedLeaderId || !isOnline) {
      stopSync("no-leader");
      return;
    }
    if (!timelineSections?.length) return;

    const tick = () => {
      const syncState = syncRef.current;
      if (!syncState.anchorUtc || !syncState.bpm || !syncState.beatsPerMeasure) {
        rafRef.current = requestAnimationFrame(tick);
        return;
      }

      const now = Date.now();
      const msPerBeat = getMsPerBeat(syncState.bpm);

      let anchorUtc = syncState.anchorUtc;
      let delayMs = syncState.delayBeats * msPerBeat;
      const effectiveAnchorUtc = anchorUtc + delayMs;
      if (syncState.delayBeats > 0 && now < effectiveAnchorUtc) {
        rafRef.current = requestAnimationFrame(tick);
        return;
      }

      if (syncState.delayBeats > 0 && now >= effectiveAnchorUtc) {
        syncRef.current.anchorUtc = effectiveAnchorUtc;
        syncRef.current.delayBeats = 0;
        anchorUtc = effectiveAnchorUtc;
        delayMs = 0;
      }

      if (pendingJumpRef.current) {
        const elapsedBeats = (now - anchorUtc) / msPerBeat;
        const beatsPerMeasure = syncState.beatsPerMeasure;
        const nextBoundaryBeat = Array.isArray(measureBoundaryBeats) && measureBoundaryBeats.length
          ? getNextBoundaryBeat(measureBoundaryBeats, elapsedBeats)
          : Math.ceil(elapsedBeats / beatsPerMeasure) * beatsPerMeasure;
        const jumpAtUtc = Number.isFinite(nextBoundaryBeat) ? anchorUtc + nextBoundaryBeat * msPerBeat : null;
        if (jumpAtUtc && now >= jumpAtUtc) {
          const targetIndex = pendingJumpRef.current.targetSectionIndex;
          const targetSection = timelineSections[targetIndex];
          const targetSectionId = targetSection?.section_id ?? targetSection?.id ?? null;
          const resolvedIndex = targetSectionId ? getSectionIndex(targetSectionId) : targetIndex;
          if (Number.isFinite(resolvedIndex)) {
            onSectionIndex(resolvedIndex, { smooth: smoothScroll });
          }
          const targetStartBeat = Number(targetSection?.start_beat ?? targetSection?.startBeat ?? 0);
          if (Number.isFinite(targetStartBeat)) {
            syncRef.current.anchorUtc = jumpAtUtc - targetStartBeat * msPerBeat;
          }
          pendingJumpRef.current = null;
        }
      }

      const currentBeat = (now - anchorUtc) / msPerBeat;
      const currentSection = timelineSections.find((section) => {
        const startBeat = Number(section.start_beat ?? section.startBeat ?? 0);
        const endBeat = Number(section.end_beat ?? section.endBeat ?? Infinity);
        return currentBeat >= startBeat && currentBeat < endBeat;
      });
      const sectionId = currentSection?.section_id ?? currentSection?.id ?? null;
      if (sectionId && String(sectionId) !== String(lastSectionIdRef.current)) {
        lastSectionIdRef.current = String(sectionId);
        const nextIndex = getSectionIndex(sectionId);
        if (Number.isFinite(nextIndex)) {
          onSectionIndex(nextIndex, { smooth: smoothScroll });
        }
      }

      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [getSectionIndex, isOnline, measureBoundaryBeats, onSectionIndex, selectedLeaderId, smoothScroll, stopSync, timelineSections]);

  return { isOnline };
}
